/**
 * Slack App Management Schemas
 *
 * Endpoint schemas for deleting a Slack app and syncing its OAuth scopes.
 */

import { z } from "zod";

// ============================================================
// Delete Slack App
// ============================================================

export const DeleteSlackAppPathVarsSchema = z.object({
  connectionId: z.string().uuid()
});

export const DeleteSlackAppResponseSchema = z.object({
  /** Whether the app was actually deleted from Slack (false = disconnected only) */
  deletedFromSlack: z.boolean(),
  connectionId: z.string(),
  /** Slack app ID — resolved via auth.test if not in settings; null when unavailable */
  appId: z.string().nullable()
});

// ============================================================
// Sync Slack App Scopes
// ============================================================

export const SyncSlackAppScopesPathVarsSchema = z.object({
  connectionId: z.string().uuid()
});

export const SyncSlackAppScopesBodySchema = z.object({
  /** Bot OAuth scopes to add to the app manifest (merged with existing, no duplicates) */
  scopes: z.array(z.string()).min(1)
});

export const SyncSlackAppScopesResponseSchema = z.object({
  synced: z.boolean(),
  /** Scopes that were actually added (excludes already-present ones) */
  addedScopes: z.array(z.string())
});

// ============================================================
// Sync Slack Manifest (full — scopes + events + version bump)
// ============================================================

export const SyncSlackManifestPathVarsSchema = z.object({
  connectionId: z.string().uuid()
});

/**
 * Optional display overrides applied to the manifest before syncing.
 * Values shown in Slack's App Directory and workspace installations.
 *
 * Slack limits:
 *   name:        max 35 chars (app name shown in App Directory)
 *   displayName: max 80 chars (the @-handle shown in Slack)
 *   description: max 140 chars
 */
export const SyncSlackManifestBodySchema = z
  .object({
    name: z.string().trim().min(1).max(35).optional(),
    displayName: z.string().trim().min(1).max(80).optional(),
    description: z.string().max(140).optional()
  })
  // Overrides are optional — an omitted body defaults to {} so the controller
  // can parse the raw param directly (no `?? {}` fallback needed).
  .default({});

export const SyncSlackManifestResponseSchema = z.object({
  synced: z.boolean(),
  /** New manifest version stored in ApiKeyConnection.settings */
  manifestVersion: z.number(),
  /** Scopes that were newly added */
  addedScopes: z.array(z.string()),
  /** Events that were newly added */
  addedEvents: z.array(z.string())
});

// ============================================================
// Refresh Slack Bot App ID
// ============================================================

export const RefreshSlackBotAppIdPathVarsSchema = z.object({
  connectionId: z.string().uuid()
});

export const RefreshSlackBotAppIdResponseSchema = z.object({
  appId: z.string().nullable(),
  stored: z.boolean()
});

// ============================================================
// Preview Slack Manifest Sync (dry-run — editable fields + delta)
// ============================================================

export const PreviewSlackManifestSyncPathVarsSchema = z.object({
  connectionId: z.string().uuid()
});

export const PreviewSlackManifestSyncResponseSchema = z.object({
  /** App name shown in Slack App Directory (display_information.name, max 35 chars) */
  name: z.string(),
  /** Bot @-handle shown in Slack (features.bot_user.display_name, max 80 chars) */
  displayName: z.string(),
  /** Short description shown in App Directory (display_information.description, max 140 chars) */
  description: z.string().nullable(),
  /** Current manifest version stored in settings (null = never synced) */
  currentVersion: z.number().nullable(),
  /** Target version after sync */
  targetVersion: z.number(),
  /** True when the bot has no config token — sync will be a no-op */
  noConfigToken: z.boolean()
});
